/* eslint-disable react-refresh/only-export-components */
// AppInsightsProvider.tsx
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { useMsal } from '@azure/msal-react'; // assumes you're using msal-react
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { createBrowserHistory } from 'history';

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
const APP_NAME = 'Global Event Calendar';

const isE2E = import.meta.env.VITE_E2E === 'true';

const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_AZURE_APPLICATION_INSIGHTS_CONNECTION_STRING as string,
    enableAutoRouteTracking: !isE2E,
    disableAjaxTracking: isE2E,
    autoTrackPageVisitTime: !isE2E,
    enableCorsCorrelation: !isE2E,
    enableRequestHeaderTracking: !isE2E,
    enableResponseHeaderTracking: !isE2E,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
  },
});
if (!isE2E) {
  appInsights.loadAppInsights();
}

// Add appName globally
appInsights.addTelemetryInitializer((envelope) => {
  envelope.baseData = envelope.baseData || {};
  envelope.baseData.properties = envelope.baseData.properties || {};
  envelope.baseData.properties.appName = APP_NAME;
  envelope.tags = envelope.tags || {};
  envelope.tags['ai.cloud.role'] = 'GEC'; // Set the cloud role to the app name
  //custom properties
  envelope.data = envelope.data || {};
  envelope.data['ml-app-code'] = 'GEC'; // Set the app code to the app name
  envelope.data['ml-app-name'] = APP_NAME; // Set the app name to the app name
});

type AppInsightsContextType = {
  appInsights: ApplicationInsights;
  reactPlugin: ReactPlugin;
};

const AppInsightsContext = createContext<AppInsightsContextType | undefined>(undefined);

export const AppInsightsProvider = ({ children }: { children: ReactNode }) => {
  const { accounts } = useMsal();

  useEffect(() => {
    const account = accounts[0]; // adjust if multiple accounts
    if (account) {
      appInsights.setAuthenticatedUserContext(
        account.username, // or account.localAccountId
        account.homeAccountId,
        true, // setAuthenticatedUserContextStoreInCookie
      );
    }
  }, [accounts]);

  const contextValue = useMemo(() => ({ appInsights, reactPlugin }), []);
  return <AppInsightsContext.Provider value={contextValue}>{children}</AppInsightsContext.Provider>;
};

export const useAppInsights = () => {
  const context = useContext(AppInsightsContext);
  if (!context) {
    throw new Error('useAppInsights must be used within an AppInsightsProvider');
  }
  return context;
};
